/**
 * Google AdSense integration module
 * 
 * This module provides functions for dynamic ad loading and management
 * based on the specifications in the ADS.md document.
 */

import config from '../config';

/**
 * Check if ads are enabled
 * @returns {boolean} True if ads are enabled
 */
export function areAdsEnabled() {
  return config.ads.enabled && 
         config.ads.googleAdSenseId && 
         typeof window !== 'undefined';
}

/**
 * Check if we should show ad placeholders
 * @returns {boolean} True if we should show placeholders
 */
export function shouldShowPlaceholders() {
  return config.ads.usePlaceholders && 
         typeof window !== 'undefined';
}

/**
 * Initialize Google AdSense
 * This should be called once when the application loads
 */
export function initializeAds() {
  // Don't load real AdSense in non-production environments
  if (config.ads.usePlaceholders) {
    console.log('Using ad placeholders in development/UAT environment');
    return;
  }

  // Only proceed if ads are enabled and we have an AdSense ID
  if (!areAdsEnabled() || typeof window === 'undefined') {
    return;
  }

  // Create AdSense script
  const script = document.createElement('script');
  script.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${config.ads.googleAdSenseId}`;
  script.async = true;
  script.crossOrigin = 'anonymous';
  document.head.appendChild(script);

  // Add event listener for ad blocker detection
  script.onerror = () => {
    console.log('AdSense blocked or failed to load');
    window.adsBlocked = true;
  };

  // Handle ad load
  script.onload = () => {
    console.log('AdSense loaded successfully');
    window.adsBlocked = false;
  };
}

/**
 * Ad format presets based on the ADS.md specification
 */
export const AD_FORMATS = {
  LEADERBOARD: {
    className: 'adsbygoogle-leaderboard',
    style: { display: 'block' },
    format: 'auto',
    responsive: true,
    dataAdSlot: config.ads.slots.leaderboard
  },
  MEDIUM_RECTANGLE: {
    className: 'adsbygoogle-medium-rectangle',
    style: { display: 'block' },
    format: 'auto',
    responsive: true,
    dataAdSlot: config.ads.slots.mediumRectangle
  },
  SIDEBAR: {
    className: 'adsbygoogle-sidebar',
    style: { display: 'block' },
    format: 'auto',
    responsive: true,
    dataAdSlot: config.ads.slots.sidebar
  },
  MOBILE_STICKY: {
    className: 'adsbygoogle-mobile-sticky',
    style: { display: 'block' },
    format: 'auto',
    responsive: true,
    dataAdSlot: config.ads.slots.mobileSticky
  },
  INLINE: {
    className: 'adsbygoogle-inline',
    style: { display: 'block' },
    format: 'auto',
    responsive: true,
    dataAdSlot: config.ads.slots.inline
  },
  FOOTER: {
    className: 'adsbygoogle-footer',
    style: { display: 'block' },
    format: 'auto',
    responsive: true,
    dataAdSlot: config.ads.slots.footer
  }
};

/**
 * Push ad display command to AdSense
 * @param {string} slotId The ad slot ID
 */
export function displayAd(slotId) {
  if (!areAdsEnabled() || typeof window === 'undefined') {
    return;
  }

  if (window.adsBlocked) {
    console.log('Ads are blocked, not displaying ad');
    return;
  }

  // Push the ad display command to AdSense
  try {
    (window.adsbygoogle = window.adsbygoogle || []).push({});
    console.log(`Ad displayed in slot: ${slotId}`);
  } catch (error) {
    console.error('Error displaying ad:', error);
  }
}

/**
 * Conditionally load ads based on premium status
 * @param {boolean} isPremium Whether the current page is for a premium contractor
 * @param {string} pageType The type of page (homepage, directory, detail, etc.)
 * @returns {boolean} Whether ads should be displayed
 */
export function shouldShowAds(isPremium, pageType) {
  if (!areAdsEnabled()) {
    return false;
  }

  // Don't show ads on premium contractor pages
  if (isPremium === true) {
    return false;
  }

  // Page-specific ad rules
  switch (pageType) {
    case 'homepage':
      return true;
    case 'directory':
      return true;
    case 'detail':
      return !isPremium; // Only show ads on non-premium contractor detail pages
    case 'lead-capture':
      return true;
    default:
      return true;
  }
}

const adsService = {
  initializeAds,
  displayAd,
  shouldShowAds,
  areAdsEnabled,
  AD_FORMATS
};

export default adsService;